
import { useState, useEffect, useRef } from "react";
import { Menu, X } from "lucide-react";
import Logo from "../ui/Logo";
import DesktopNavigation from "./DesktopNavigation";
import MobileNavigation from "./MobileNavigation";
import { useNavigation } from "./Navigation";
import AuthStatus from "../auth/AuthStatus";

// Create a context to share the header height
export const useHeaderHeight = () => {
  const [headerHeight, setHeaderHeight] = useState(0);
  
  useEffect(() => {
    // Get the header element by ID
    const headerElement = document.getElementById("site-header");
    if (headerElement) {
      // Function to measure and set header height
      const updateHeaderHeight = () => {
        const height = headerElement.offsetHeight;
        setHeaderHeight(height);
        
        // Update CSS variable for header height
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      };
      
      // Initial measurement
      updateHeaderHeight();
      
      // Create a ResizeObserver to track changes to the header's size
      const resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      
      // Start observing the header
      resizeObserver.observe(headerElement);
      
      // Also handle window resize events for additional reliability
      window.addEventListener('resize', updateHeaderHeight);
      
      // Clean up
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateHeaderHeight);
      };
    }
  }, []);
  
  return headerHeight;
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { mainNavItems, serviceNavItems } = useNavigation();
  const headerRef = useRef<HTMLElement>(null);
  
  // Explicitly call the hook to ensure it runs
  useHeaderHeight();
  
  return (
    <header id="site-header" ref={headerRef} className="bg-white shadow-sm fixed top-0 left-0 right-0 w-full z-50">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Logo - now on the left */}
          <Logo className="max-w-[120px]" />
          
          {/* Desktop Navigation - now inline with logo */}
          <div className="hidden md:flex items-center gap-4">
            <DesktopNavigation 
              mainNavItems={mainNavItems} 
              serviceNavItems={serviceNavItems} 
            />
            <AuthStatus />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <AuthStatus />
            <button 
              className="ml-2 text-kaija-slate" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <MobileNavigation
        mainNavItems={mainNavItems}
        serviceNavItems={serviceNavItems}
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </header>
  );
};

export default Header;
